import { Badge, Tabs, Tooltip } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';
import { isEqual } from 'lodash';
import React from 'react';
import { RouteComponentProps } from 'react-router';
import { withRouter } from 'react-router-dom';
import { WithTranslation, withTranslation } from 'react-i18next';
import i18n from '../../../i18n/config';
import { Action, ActionImportance, ActionSourceType, ActionStatus, IncidentTypeLabel } from '../../../models';
import { InspectionListItem, MaintenanceLogItem, MyWorkActivity, RiskAssessmentResultItem } from '../../../services';
import { ActionImportanceLabel, ActionSourceLabel, COLORS } from '../../../shared';
import { getDate, isActionOverdue } from '../../../utils';
import { HasTable } from '../../molecules';

const { TabPane } = Tabs;

const TABLE_HEADER_HEIGHT = 52;
const TABLE_ITEM_HEIGHT = 50;
const TABLE_PAGINATION_HEIGHT = 32 + 16 * 2;

interface MyOpenWorkProps extends WithTranslation, RouteComponentProps {
  userId: number;
  onTabChange?: (key: string) => void;
}

interface MyOpenWorkState {
  actions: Action[];
  inspections: InspectionListItem[];
  maintenances: MaintenanceLogItem[];
  riskAssessments: RiskAssessmentResultItem[];
  loadingActions: boolean;
  loadingInspections: boolean;
  loadingMaintenances: boolean;
  loadingRiskAssessments: boolean;
  activeTab: string;
  totalActions: number;
  totalInspections: number;
  totalMaintenances: number;
  totalRiskAssessments: number;
  pageActions: number;
}

const importanceDot = (importance?: ActionImportance) => {
  if (!importance) return null;
  const colorMap: Record<ActionImportance, string> = {
    [ActionImportance.LOW]: COLORS.GREEN,
    [ActionImportance.MEDIUM]: COLORS.YELLOW,
    [ActionImportance.HIGH]: COLORS.LIGHT_RED,
  };
  return (
    <>
      <Badge color={colorMap[importance]} />
      {i18n.t(ActionImportanceLabel[importance])}
    </>
  );
};

const statusBadge = (status?: string) => {
  if (!status) return null;
  const isOverdue = status === ActionStatus.OVERDUE || status === 'OVERDUE';
  const color = isOverdue
    ? COLORS.LIGHT_RED
    : status === ActionStatus.OPEN || status === 'OPEN'
      ? COLORS.WARNING
      : COLORS.GREEN;
  const label = isOverdue
    ? i18n.t('action.overdue', 'Overdue')
    : status === 'OPEN'
      ? i18n.t('action.open', 'Open')
      : i18n.t('shared.closed', 'Closed');
  return (
    <>
      <Badge color={color} />
      {label}
    </>
  );
};

const complianceFormBadge = (completed: boolean) => (
  <Tooltip
    title={
      completed
        ? i18n.t('action.regulatoryQuestions.completed', 'Regulatory questions completed')
        : i18n.t('action.regulatoryQuestions.notCompleted', 'Regulatory questions not completed')
    }
  >
    <Badge color={completed ? COLORS.GREEN : COLORS.LIGHT_RED} />
  </Tooltip>
);

const unique = <T,>(arr: (T | undefined | null)[]): T[] => Array.from(new Set(arr.filter((v): v is T => v != null)));

class MyOpenWork extends React.Component<MyOpenWorkProps, MyOpenWorkState> {
  private actionFilters: {
    pageNumber: number;
    pageSize: number;
    sort?: { fieldName: string; direction: string };
    status?: number[];
    sourceType?: ActionSourceType[];
  } = { pageNumber: 1, pageSize: 10, sort: { fieldName: 'status', direction: 'ASC' } };

  private inspectionFilters: {
    pageNumber: number;
    pageSize: number;
    sort?: { fieldName: string; direction: string };
    closed?: boolean[];
  } = { pageNumber: 1, pageSize: 10, sort: { fieldName: 'closed', direction: 'ASC' } };

  private maintenanceFilters: {
    pageNumber: number;
    pageSize: number;
    sort?: { fieldName: string; direction: string };
    status?: string[];
  } = { pageNumber: 1, pageSize: 10, sort: { fieldName: 'status', direction: 'ASC' } };

  private riskAssessmentFilters: {
    pageNumber: number;
    pageSize: number;
    sort?: { fieldName: string; direction: string };
    types?: string[];
  } = { pageNumber: 1, pageSize: 10 };

  state: MyOpenWorkState = {
    actions: [],
    inspections: [],
    maintenances: [],
    riskAssessments: [],
    loadingActions: true,
    loadingInspections: true,
    loadingMaintenances: true,
    loadingRiskAssessments: true,
    activeTab: 'actions',
    totalActions: 0,
    totalInspections: 0,
    totalMaintenances: 0,
    totalRiskAssessments: 0,
    pageActions: 1,
  };

  componentDidMount() {
    this.loadAll();
  }

  loadAll = () => {
    this.loadActions();
    this.loadInspections();
    this.loadMaintenances();
    this.loadRiskAssessments();
  };

  loadActions = () => {
    const { userId } = this.props;
    const { pageNumber, sort, status, sourceType } = this.actionFilters;
    this.setState({ loadingActions: true, pageActions: pageNumber });
    MyWorkActivity.getMyActions(userId, pageNumber, sort, status, sourceType)
      .then(({ data }) =>
        this.setState({
          actions: data.content,
          totalActions: data.totalElements,
          loadingActions: false,
        })
      )
      .catch(() => this.setState({ loadingActions: false }));
  };

  onActionTableChange = (
    pagination: any,
    filters: Record<string, (string | number)[] | null>,
    sorter: SorterResult<Action> | SorterResult<Action>[]
  ) => {
    const previousStatus = this.actionFilters.status;
    const previousSourceType = this.actionFilters.sourceType;

    if (!Array.isArray(sorter) && sorter.columnKey && sorter.order) {
      this.actionFilters.sort = {
        fieldName: String(sorter.columnKey),
        direction: sorter.order === 'ascend' ? 'ASC' : 'DESC',
      };
    } else {
      this.actionFilters.sort = undefined;
    }

    const statusVals = filters['status'] as string[] | null;
    this.actionFilters.status = statusVals?.length
      ? statusVals.map((v) => (v === 'OPEN' ? 0 : v === 'CLOSED' ? 1 : 2))
      : undefined;

    const sourceVals = filters['source'] as ActionSourceType[] | null;
    this.actionFilters.sourceType = sourceVals?.length ? sourceVals : undefined;

    const filtersChanged =
      !isEqual(previousStatus, this.actionFilters.status) ||
      !isEqual(previousSourceType, this.actionFilters.sourceType);
    this.actionFilters.pageNumber = filtersChanged ? 1 : ((pagination as any).current ?? 1);
    this.actionFilters.pageSize = (pagination as any).pageSize ?? 10;

    this.loadActions();
  };

  loadInspections = () => {
    const { userId } = this.props;
    const { pageNumber, sort, closed } = this.inspectionFilters;
    this.setState({ loadingInspections: true });
    MyWorkActivity.getMyInspections(userId, pageNumber, sort, closed)
      .then(({ data }) => {
        this.setState({ inspections: data.content, totalInspections: data.totalElements, loadingInspections: false });
      })
      .catch(() => this.setState({ loadingInspections: false }));
  };

  onInspectionTableChange = (
    pagination: any,
    filters: Record<string, (string | number | boolean)[] | null>,
    sorter: SorterResult<InspectionListItem> | SorterResult<InspectionListItem>[]
  ) => {
    const previousClosed = this.inspectionFilters.closed;

    const SORT_FIELD: Record<string, string> = {
      id: 'id',
      status: 'closed',
      name: 'name',
      nextInspection: 'nextInspection',
      scheduled: 'scheduled',
    };

    if (!Array.isArray(sorter) && sorter.columnKey && sorter.order) {
      this.inspectionFilters.sort = {
        fieldName: SORT_FIELD[String(sorter.columnKey)] ?? String(sorter.columnKey),
        direction: sorter.order === 'ascend' ? 'ASC' : 'DESC',
      };
    } else {
      this.inspectionFilters.sort = { fieldName: 'closed', direction: 'ASC' };
    }

    const statusVals = filters['status'] as string[] | null;
    if (statusVals?.length) {
      const closedVals: boolean[] = [];
      if (statusVals.includes('CLOSED')) closedVals.push(true);
      if (statusVals.includes('OPEN') || statusVals.includes('OVERDUE')) closedVals.push(false);
      this.inspectionFilters.closed = closedVals;
    } else {
      this.inspectionFilters.closed = undefined;
    }

    const filtersChanged = !isEqual(previousClosed, this.inspectionFilters.closed);
    this.inspectionFilters.pageNumber = filtersChanged ? 1 : (pagination.current ?? 1);
    this.inspectionFilters.pageSize = pagination.pageSize ?? 10;

    this.loadInspections();
  };

  loadMaintenances = () => {
    const { userId } = this.props;
    const { pageNumber, sort, status } = this.maintenanceFilters;
    this.setState({ loadingMaintenances: true });
    MyWorkActivity.getMyMaintenances(userId, pageNumber, sort, status)
      .then(({ data }) =>
        this.setState({
          maintenances: data.content,
          totalMaintenances: data.totalElements,
          loadingMaintenances: false,
        })
      )
      .catch(() => this.setState({ loadingMaintenances: false }));
  };

  onMaintenanceTableChange = (
    pagination: any,
    filters: Record<string, (string | number | boolean)[] | null>,
    sorter: SorterResult<MaintenanceLogItem> | SorterResult<MaintenanceLogItem>[]
  ) => {
    const previousStatus = this.maintenanceFilters.status;

    if (!Array.isArray(sorter) && sorter.columnKey && sorter.order) {
      this.maintenanceFilters.sort = {
        fieldName: String(sorter.columnKey),
        direction: sorter.order === 'ascend' ? 'ASC' : 'DESC',
      };
    } else {
      this.maintenanceFilters.sort = { fieldName: 'status', direction: 'ASC' };
    }

    const statusVals = filters['status'] as string[] | null;
    this.maintenanceFilters.status = statusVals?.length ? statusVals : undefined;

    const filtersChanged = !isEqual(previousStatus, this.maintenanceFilters.status);
    this.maintenanceFilters.pageNumber = filtersChanged ? 1 : (pagination.current ?? 1);
    this.maintenanceFilters.pageSize = pagination.pageSize ?? 10;

    this.loadMaintenances();
  };

  loadRiskAssessments = () => {
    const { userId } = this.props;
    const { pageNumber, sort, types } = this.riskAssessmentFilters;
    this.setState({ loadingRiskAssessments: true });
    MyWorkActivity.getMyRiskAssessments(userId, pageNumber, sort, types)
      .then(({ data }) =>
        this.setState({
          riskAssessments: data.content,
          totalRiskAssessments: data.totalElements,
          loadingRiskAssessments: false,
        })
      )
      .catch(() => this.setState({ loadingRiskAssessments: false }));
  };

  onRiskAssessmentTableChange = (
    pagination: any,
    filters: Record<string, (string | number | boolean)[] | null>,
    sorter: SorterResult<RiskAssessmentResultItem> | SorterResult<RiskAssessmentResultItem>[]
  ) => {
    const previousTypes = this.riskAssessmentFilters.types;

    if (!Array.isArray(sorter) && sorter.columnKey && sorter.order) {
      this.riskAssessmentFilters.sort = {
        fieldName: String(sorter.columnKey),
        direction: sorter.order === 'ascend' ? 'ASC' : 'DESC',
      };
    } else {
      this.riskAssessmentFilters.sort = undefined;
    }

    const typeVals = filters['type'] as string[] | null;
    this.riskAssessmentFilters.types = typeVals?.length ? typeVals : undefined;

    const filtersChanged = !isEqual(previousTypes, this.riskAssessmentFilters.types);
    this.riskAssessmentFilters.pageNumber = filtersChanged ? 1 : (pagination.current ?? 1);
    this.riskAssessmentFilters.pageSize = pagination.pageSize ?? 10;

    this.loadRiskAssessments();
  };

  navigateToAction = (action: Action) => {
    if (action.riskAssessmentResult && process.env.REACT_APP_RISK_ASSESSMENTS_URL) {
      window.location.href = `${process.env.REACT_APP_RISK_ASSESSMENTS_URL}risk-assessment/action/view/${action.id}`;
    } else {
      this.props.history.push(`/events/action/${action.incidentId}?id=${action.id}`);
    }
  };

  navigateToInspection = (inspection: InspectionListItem) => {
    if (process.env.REACT_APP_EQUIPMENT_URL) {
      window.location.href = `${process.env.REACT_APP_EQUIPMENT_URL}inspection/view/${inspection.id}`;
    }
  };

  navigateToMaintenance = (maintenance: MaintenanceLogItem) => {
    if (process.env.REACT_APP_EQUIPMENT_URL) {
      const path = maintenance.equipment?.id
        ? `equipment/view/${maintenance.equipment.id}?maintenanceId=${maintenance.id}`
        : `maintenance/view/${maintenance.id}`;
      window.location.href = `${process.env.REACT_APP_EQUIPMENT_URL}${path}`;
    }
  };

  navigateToRiskAssessment = (ra: RiskAssessmentResultItem) => {
    if (process.env.REACT_APP_RISK_ASSESSMENTS_URL) {
      const path =
        ra.type === 'RISK_ASSESSMENT_RESULT' ? `risk-assessment-result/view/${ra.id}` : `risk-assessment/view/${ra.id}`;
      window.location.href = `${process.env.REACT_APP_RISK_ASSESSMENTS_URL}${path}`;
    }
  };

  getActionColumns = (): ColumnProps<Action>[] => {
    return [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 60,
        sorter: true,
      },
      {
        title: i18n.t('shared.status', 'Status'),
        key: 'status',
        render: (_: any, record: Action) => {
          const derived =
            record.status === ActionStatus.OPEN && record.dueDate && isActionOverdue(record.status, record.dueDate)
              ? 'OVERDUE'
              : record.status;
          return statusBadge(derived);
        },
        width: 110,
        sorter: true,
        defaultSortOrder: 'ascend',
        filters: [
          { text: i18n.t('action.open', 'Open'), value: 'OPEN' },
          { text: i18n.t('action.overdue', 'Overdue'), value: 'OVERDUE' },
          { text: i18n.t('action.close', 'Closed'), value: 'CLOSED' },
        ],
      },
      {
        title: i18n.t('action.complianceForm', 'Compliance form'),
        key: 'complianceFormStatus',
        render: (_: any, record: Action) =>
          record.status === ActionStatus.CLOSED && record.complianceFormCompleted !== undefined
            ? complianceFormBadge(record.complianceFormCompleted)
            : '-',
        width: 110,
      },
      {
        title: i18n.t('shared.requiredAction', 'Required action'),
        dataIndex: 'requiredAction',
        width: 150,
        key: 'requiredAction',
        sorter: true,
        render: (text: string) => (
          <div
            title={text}
            style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {text}
          </div>
        ),
      },
      {
        title: i18n.t('action.source', 'Source'),
        key: 'source',
        render: (_: any, record: Action) => {
          if (record.riskAssessmentResult) {
            return `${i18n.t('shared.riskAssessment', 'Risk assessment')} #${record.riskAssessmentResult.id}`;
          }
          const typeLabel = record.incidentType
            ? i18n.t(IncidentTypeLabel[record.incidentType])
            : i18n.t(ActionSourceLabel[record.sourceType] ?? '');
          return record.incidentId ? `${typeLabel} #${record.incidentId}` : typeLabel;
        },
        width: 180,
        ellipsis: true,
        filters: [
          {
            text: i18n.t(ActionSourceLabel[ActionSourceType.EVENT] ?? 'Event', 'Event'),
            value: ActionSourceType.EVENT,
          },
          {
            text: i18n.t(ActionSourceLabel[ActionSourceType.RISK_ASSESSMENT] ?? 'Risk assessment', 'Risk assessment'),
            value: ActionSourceType.RISK_ASSESSMENT,
          },
        ],
      },
      {
        title: i18n.t('shared.dueDate', 'Due date'),
        dataIndex: 'dueDate',
        key: 'dueDate',
        render: (date: string, record) =>
          date ? (
            <span
              style={{
                color:
                  date < new Date().toISOString().slice(0, 10) && record.status !== ActionStatus.CLOSED
                    ? COLORS.LIGHT_RED
                    : undefined,
              }}
            >
              {getDate(date)}
            </span>
          ) : (
            '-'
          ),
        width: 120,
        sorter: true,
      },
      {
        title: i18n.t('shared.createdOn', 'Created on'),
        dataIndex: 'createdOn',
        key: 'createdOn',
        render: (date: string) => (date ? getDate(date) : '-'),
        width: 120,
        sorter: true,
      },
      {
        title: i18n.t('shared.priority', 'Priority'),
        dataIndex: 'importance',
        key: 'importance',
        render: (importance: ActionImportance) => importanceDot(importance),
        width: 110,
        sorter: true,
        filters: [
          { text: i18n.t(ActionImportanceLabel[ActionImportance.LOW]), value: ActionImportance.LOW },
          { text: i18n.t(ActionImportanceLabel[ActionImportance.MEDIUM]), value: ActionImportance.MEDIUM },
          { text: i18n.t(ActionImportanceLabel[ActionImportance.HIGH]), value: ActionImportance.HIGH },
        ],
      },
    ];
  };

  getInspectionColumns = (): ColumnProps<InspectionListItem>[] => {
    const { inspections } = this.state;
    const questionnaires = unique(inspections.map((i) => i.chatbotQuestionnaire?.name));

    return [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 60,
        sorter: true,
      },
      {
        title: i18n.t('shared.status', 'Status'),
        key: 'status',
        render: (_: any, record: InspectionListItem) => {
          const status = record.closed ? 'CLOSED' : record.overdue ? 'OVERDUE' : 'OPEN';
          return statusBadge(status);
        },
        width: 110,
        sorter: true,
        defaultSortOrder: 'ascend',
        filters: [
          { text: i18n.t('action.open', 'Open'), value: 'OPEN' },
          { text: i18n.t('action.overdue', 'Overdue'), value: 'OVERDUE' },
          { text: i18n.t('action.close', 'Closed'), value: 'CLOSED' },
        ],
      },
      {
        title: i18n.t('shared.name', 'Name'),
        dataIndex: 'name',
        width: 150,
        key: 'name',
        ellipsis: true,
        sorter: true,
      },
      {
        title: i18n.t('shared.questionnaire', 'Questionnaire'),
        key: 'chatbotQuestionnaire',
        width: 100,
        render: (_: any, record: InspectionListItem) => record.chatbotQuestionnaire?.name || '-',
        ellipsis: true,
        filters: questionnaires.map((q) => ({ text: q, value: q })),
        onFilter: (value, record) => record.chatbotQuestionnaire?.name === value,
      },
      {
        title: i18n.t('inspection.header.scheduled', 'Scheduled'),
        dataIndex: 'scheduled',
        key: 'scheduled',
        width: 100,
        render: (val: boolean) => (val == null ? '-' : val ? i18n.t('shared.yes', 'Yes') : i18n.t('shared.no', 'No')),
        sorter: true,
        filters: [
          { text: i18n.t('shared.yes', 'Yes'), value: true },
          { text: i18n.t('shared.no', 'No'), value: false },
        ],
        onFilter: (value, record) => Boolean(record.scheduled) === value,
      },
      {
        title: i18n.t('inspection.header.upcomingDate', 'Upcoming date'),
        dataIndex: 'nextInspection',
        key: 'nextInspection',
        render: (date: string, record) =>
          date ? (
            <span
              style={{
                color: date < new Date().toISOString().slice(0, 10) && !record.closed ? COLORS.LIGHT_RED : undefined,
              }}
            >
              {getDate(date)}
            </span>
          ) : (
            '-'
          ),
        width: 120,
        sorter: true,
      },
    ];
  };

  getMaintenanceColumns = (): ColumnProps<MaintenanceLogItem>[] => {
    const { maintenances } = this.state;
    const equipmentNames = unique(maintenances.map((m) => m.equipment?.name ?? m.name));
    const creatorNames = unique(maintenances.map((m) => m.creatorUser?.fullName));
    const chatbotNames = unique(maintenances.map((m) => m.chatbot?.name));
    return [
      {
        title: i18n.t('maintenance.id', 'ID'),
        dataIndex: 'id',
        key: 'id',
        width: 60,
        sorter: true,
      },
      {
        title: i18n.t('shared.status', 'Status'),
        dataIndex: 'status',
        key: 'status',
        render: (status: string) => statusBadge(status),
        width: 110,
        sorter: true,
        defaultSortOrder: 'ascend',
        filters: [
          { text: i18n.t('action.open', 'Open'), value: 'OPEN' },
          { text: i18n.t('action.overdue', 'Overdue'), value: 'OVERDUE' },
          { text: i18n.t('shared.done', 'Done'), value: 'DONE' },
        ],
      },
      {
        title: i18n.t('maintenance.equipment', 'Equipment'),
        key: 'equipment',
        render: (_: any, record: MaintenanceLogItem) => record.equipment?.name || record.name,
        ellipsis: true,
        sorter: true,
        filters: equipmentNames.map((n) => ({ text: n, value: n })),
        onFilter: (value, record) => (record.equipment?.name ?? record.name) === value,
      },
      {
        title: i18n.t('maintenance.operator', 'Operator'),
        key: 'creatorUser',
        render: (_: any, record: MaintenanceLogItem) => record.creatorUser?.fullName || '-',
        width: 140,
        sorter: true,
        filters: creatorNames.map((n) => ({ text: n, value: n })),
        onFilter: (value, record) => record.creatorUser?.fullName === value,
      },
      {
        title: i18n.t('shared.questionnaire', 'Questionnaire'),
        key: 'chatbot',
        render: (_: any, record: MaintenanceLogItem) => record.chatbot?.name || '-',
        ellipsis: true,
        filters: chatbotNames.map((n) => ({ text: n, value: n })),
        onFilter: (value, record) => record.chatbot?.name === value,
      },
      {
        title: i18n.t('maintenance.dueDate', 'Due date'),
        dataIndex: 'dueDate',
        key: 'dueDate',
        render: (date: string) =>
          date ? (
            <span style={{ color: date < new Date().toISOString().slice(0, 10) ? COLORS.LIGHT_RED : undefined }}>
              {getDate(date)}
            </span>
          ) : (
            '-'
          ),
        width: 120,
        sorter: true,
      },
      {
        title: i18n.t('maintenance.createdOn', 'Created on'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (date: string) => (date ? getDate(date) : '-'),
        width: 120,
        sorter: true,
      },
    ];
  };

  getRiskAssessmentColumns = (): ColumnProps<RiskAssessmentResultItem>[] => {
    const { riskAssessments } = this.state;
    const chatbotNames = unique(riskAssessments.map((r) => r.chatbot?.name));
    const creatorNames = unique(riskAssessments.map((r) => r.createdBy?.fullName));

    return [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 60,
        sorter: true,
      },
      {
        title: i18n.t('shared.status', 'Status'),
        key: 'status',
        render: (_: any, record: RiskAssessmentResultItem) => {
          const today = new Date().toISOString().slice(0, 10);
          const isClosed = record.open === '0';
          const status = isClosed ? 'CLOSED' : record.dueDate && record.dueDate < today ? 'OVERDUE' : 'OPEN';
          return statusBadge(status);
        },
        width: 110,
        sorter: true,
        filters: [
          { text: i18n.t('action.open', 'Open'), value: 'OPEN' },
          { text: i18n.t('action.overdue', 'Overdue'), value: 'OVERDUE' },
          { text: i18n.t('action.close', 'Closed'), value: 'CLOSED' },
        ],
        onFilter: (value, record) => {
          const today = new Date().toISOString().slice(0, 10);
          const isOpen = record.open === '1';
          const status = !isOpen ? 'CLOSED' : record.dueDate && record.dueDate < today ? 'OVERDUE' : 'OPEN';
          return status === value;
        },
      },
      {
        title: i18n.t('shared.name', 'Name'),
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        sorter: true,
      },
      {
        title: i18n.t('shared.type', 'Type'),
        dataIndex: 'type',
        key: 'type',
        render: (type: string) =>
          type === 'RISK_ASSESSMENT_RESULT'
            ? i18n.t('riskAssessment.result', 'RA Result')
            : i18n.t('riskAssessment.riskAssessment', 'Risk Assessment'),
        width: 130,
        sorter: true,
        filters: [
          { text: i18n.t('riskAssessment.riskAssessment', 'Risk Assessment'), value: 'RISK_ASSESSMENT' },
          { text: i18n.t('riskAssessment.result', 'RA Result'), value: 'RISK_ASSESSMENT_RESULT' },
        ],
      },
      {
        title: i18n.t('shared.questionnaire', 'Questionnaire'),
        key: 'chatbotQuestionnaire',
        render: (_: any, record: RiskAssessmentResultItem) => record.chatbot?.name || '-',
        ellipsis: true,
        filters: chatbotNames.map((n) => ({ text: n, value: n })),
        onFilter: (value, record) => record.chatbot?.name === value,
      },
      {
        title: i18n.t('shared.lastCompletedOn', 'Last completed on'),
        dataIndex: 'lastCompletedDate',
        key: 'lastCompletedDate',
        render: (date: string) => (date ? getDate(date) : '-'),
        width: 140,
        sorter: true,
      },
      {
        title: i18n.t('shared.createdBy', 'Created by'),
        key: 'createdBy',
        render: (_: any, record: RiskAssessmentResultItem) => record.createdBy?.fullName || '-',
        width: 140,
        sorter: true,
        filters: creatorNames.map((n) => ({ text: n, value: n })),
        onFilter: (value, record) => record.createdBy?.fullName === value,
      },
      {
        title: i18n.t('shared.dueDate', 'Due date'),
        dataIndex: 'dueDate',
        key: 'dueDate',
        render: (date: string) =>
          date ? (
            <span style={{ color: date < new Date().toISOString().slice(0, 10) ? COLORS.LIGHT_RED : undefined }}>
              {getDate(date)}
            </span>
          ) : (
            '-'
          ),
        width: 120,
        sorter: true,
      },
    ];
  };

  tabLabel = (key: string) => <span>{key}&nbsp;</span>;

  render() {
    const {
      actions,
      inspections,
      maintenances,
      riskAssessments,
      loadingActions,
      loadingInspections,
      loadingMaintenances,
      loadingRiskAssessments,
      totalActions,
      totalInspections,
      totalMaintenances,
      totalRiskAssessments,
    } = this.state;
    const { onTabChange } = this.props;

    return (
      <div
        style={{
          backgroundColor: COLORS.WHITE,
          padding: '10px',
          border: `1px solid ${COLORS.GRAY_ACCENT_LIGHT}`,
          flex: 1,
        }}
        className="d-flex flex-column shadow-sm overflow-hidden"
      >
        <h3 className={'ant-descriptions-title'} style={{ marginLeft: 9, marginTop: 3 }}>
          {i18n.t('user.statistics.myActivity', 'MY ACTIVITY')}
        </h3>
        <Tabs
          defaultActiveKey="actions"
          size="small"
          className="my-activity-tabs"
          onChange={(key) => {
            this.setState({ activeTab: key });
            onTabChange?.(key);
          }}
        >
          <TabPane tab={this.tabLabel(i18n.t('shared.actions', 'Actions'))} key="actions">
            <HasTable<Action>
              rowKey="id"
              data={actions}
              columns={this.getActionColumns()}
              loading={loadingActions}
              tableHeaderHeight={TABLE_HEADER_HEIGHT}
              tableItemHeight={TABLE_ITEM_HEIGHT}
              tablePaginationHeight={TABLE_PAGINATION_HEIGHT}
              size={'small'}
              isApiPaginated
              total={totalActions}
              defaultPageNumber={this.actionFilters.pageNumber}
              onChange={this.onActionTableChange}
              onPageSizeChange={(size: number) => {
                this.actionFilters.pageSize = size;
              }}
              onRow={(record) => ({ onClick: () => this.navigateToAction(record), style: { cursor: 'pointer' } })}
            />
          </TabPane>
          <TabPane tab={this.tabLabel(i18n.t('inspection.inspections', 'Inspections'))} key="inspections">
            <HasTable<InspectionListItem>
              rowKey="id"
              data={inspections}
              columns={this.getInspectionColumns()}
              loading={loadingInspections}
              tableHeaderHeight={TABLE_HEADER_HEIGHT}
              tableItemHeight={TABLE_ITEM_HEIGHT}
              tablePaginationHeight={TABLE_PAGINATION_HEIGHT}
              size={'small'}
              isApiPaginated
              total={totalInspections}
              defaultPageNumber={this.inspectionFilters.pageNumber}
              onChange={this.onInspectionTableChange}
              onPageSizeChange={(size: number) => {
                this.inspectionFilters.pageSize = size;
              }}
              onRow={(record) => ({ onClick: () => this.navigateToInspection(record), style: { cursor: 'pointer' } })}
            />
          </TabPane>
          <TabPane tab={this.tabLabel(i18n.t('maintenance.maintenances', 'Maintenances'))} key="maintenances">
            <HasTable<MaintenanceLogItem>
              rowKey="id"
              data={maintenances}
              columns={this.getMaintenanceColumns()}
              loading={loadingMaintenances}
              tableHeaderHeight={TABLE_HEADER_HEIGHT}
              tableItemHeight={TABLE_ITEM_HEIGHT}
              tablePaginationHeight={TABLE_PAGINATION_HEIGHT}
              size={'small'}
              isApiPaginated
              total={totalMaintenances}
              defaultPageNumber={this.maintenanceFilters.pageNumber}
              onChange={this.onMaintenanceTableChange}
              onPageSizeChange={(size: number) => {
                this.maintenanceFilters.pageSize = size;
              }}
              onRow={(record) => ({ onClick: () => this.navigateToMaintenance(record), style: { cursor: 'pointer' } })}
            />
          </TabPane>
          <TabPane
            tab={this.tabLabel(i18n.t('riskAssessment.riskAssessments', 'Risk Assessments'))}
            key="riskAssessments"
          >
            <HasTable<RiskAssessmentResultItem>
              rowKey="id"
              data={riskAssessments}
              columns={this.getRiskAssessmentColumns()}
              loading={loadingRiskAssessments}
              tableHeaderHeight={TABLE_HEADER_HEIGHT}
              tableItemHeight={TABLE_ITEM_HEIGHT}
              tablePaginationHeight={TABLE_PAGINATION_HEIGHT}
              size={'small'}
              isApiPaginated
              total={totalRiskAssessments}
              defaultPageNumber={this.riskAssessmentFilters.pageNumber}
              onChange={this.onRiskAssessmentTableChange}
              onPageSizeChange={(size: number) => {
                this.riskAssessmentFilters.pageSize = size;
              }}
              onRow={(record) => ({
                onClick: () => this.navigateToRiskAssessment(record),
                style: { cursor: 'pointer' },
              })}
            />
          </TabPane>
        </Tabs>
      </div>
    );
  }
}

export default withRouter(withTranslation()(MyOpenWork));
